import { Fragment } from 'react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Simple markdown renderer for song details
 * Supports: bold, italic, ## headings, ### sub-headings, bullet lists
 */
export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  const renderMarkdown = (text: string): JSX.Element[] => {
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    let listItems: string[] = [];
    let lineIndex = 0;

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} className="list-disc list-inside space-y-1 my-3 text-gray-300">
            {listItems.map((item, idx) => (
              <li key={idx}>{processInlineMarkdown(item)}</li>
            ))}
          </ul>
        );
        listItems = [];
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      lineIndex = i;

      // Sub-sub-heading (###)
      if (line.startsWith('### ')) {
        flushList();
        const headingText = line.substring(4);
        elements.push(
          <h4 key={`h4-${i}`} className="text-base font-semibold text-gray-200 mt-4 mb-2">
            {processInlineMarkdown(headingText)}
          </h4>
        );
        continue;
      }

      // Sub-heading (##)
      if (line.startsWith('## ')) {
        flushList();
        const headingText = line.substring(3);
        elements.push(
          <h3 key={`h3-${i}`} className="text-lg font-semibold text-gray-100 mt-5 mb-2">
            {processInlineMarkdown(headingText)}
          </h3>
        );
        continue;
      }

      // Bullet list items (- or *)
      if (line.match(/^[\s]*[-*]\s+/)) {
        const itemText = line.replace(/^[\s]*[-*]\s+/, '');
        listItems.push(itemText);
        continue;
      }

      // Flush any pending list before non-list content
      flushList();

      // Empty line
      if (line.trim() === '') {
        elements.push(<br key={`br-${i}`} />);
        continue;
      }

      // Regular paragraph
      elements.push(
        <p key={`p-${i}`} className="text-gray-300 leading-relaxed">
          {processInlineMarkdown(line)}
        </p>
      );
    }

    // Flush any remaining list items
    flushList();

    return elements;
  };

  /**
   * Process inline markdown (bold, italic, [section markers]) within a line
   */
  const processInlineMarkdown = (text: string): (string | JSX.Element)[] => {
    const parts: (string | JSX.Element)[] = [];
    let remaining = text;
    let keyIndex = 0;

    while (remaining.length > 0) {
      // Section markers [Verse 1], [Chorus], etc. - typically at start of line
      const sectionMatch = remaining.match(/^\[([^\]]+)\]/);
      if (sectionMatch) {
        parts.push(
          <span key={`section-${keyIndex++}`} className="text-monster-blue font-semibold tracking-wide">
            [{sectionMatch[1]}]
          </span>
        );
        remaining = remaining.substring(sectionMatch[0].length);
        continue;
      }

      // Bold (**text**)
      const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
      if (boldMatch) {
        parts.push(
          <strong key={`bold-${keyIndex++}`} className="font-bold text-white">
            {boldMatch[1]}
          </strong>
        );
        remaining = remaining.substring(boldMatch[0].length);
        continue;
      }

      // Italic (*text* or _text_)
      const italicMatch = remaining.match(/^[*_](.+?)[*_]/);
      if (italicMatch) {
        parts.push(
          <em key={`italic-${keyIndex++}`} className="italic">
            {italicMatch[1]}
          </em>
        );
        remaining = remaining.substring(italicMatch[0].length);
        continue;
      }

      // Regular text until next markdown symbol
      const nextSpecial = remaining.search(/[*_\[]/);
      if (nextSpecial === -1) {
        // No more special characters, add remaining text and exit
        parts.push(remaining);
        break;
      } else if (nextSpecial === 0) {
        // Special character at start but didn't match any pattern
        // Treat it as literal text and move forward one character
        parts.push(remaining[0]);
        remaining = remaining.substring(1);
      } else {
        // Normal case: add text before special character
        parts.push(remaining.substring(0, nextSpecial));
        remaining = remaining.substring(nextSpecial);
      }
    }

    return parts;
  };

  return (
    <div className={className}>
      {renderMarkdown(content).map((element, idx) => (
        <Fragment key={idx}>{element}</Fragment>
      ))}
    </div>
  );
}
